import { useFilterStore } from '../stores/filterStore.js';
import { getInternalFilterParams } from './filterMapping.js';

/**
 * URL Handler - Manages URL parameter synchronization for shareable filtered views
 */
export class URLHandler {
  constructor() {
    this.isInitialized = false;
  }

  /**
   * Initialize URL handling - should be called on application startup
   */
  init() {
    if (this.isInitialized) {
      console.warn('URLHandler: Already initialized');
      return;
    }

    // Load initial state from URL on app startup - DISABLED to reset filters on refresh
    // this.loadFromURL();
    
    // Set up browser navigation handlers
    this.setupBrowserNavigation();
    
    // Set up filter change listeners - DISABLED to prevent URL updates
    // this.setupFilterChangeListeners();
    
    this.isInitialized = true;
    console.log('URLHandler: Initialized with URL parameter support');
  }

  /**
   * Load filter state from current URL parameters
   */
  loadFromURL() {
    try {
      const { loadFromURL } = useFilterStore.getState();
      loadFromURL();
    } catch (error) {
      console.error('URLHandler: Error loading from URL:', error);
    }
  }

  /**
   * Update URL with current filter state
   */
  updateURL() {
    try {
      const { updateURL } = useFilterStore.getState();
      updateURL();
    } catch (error) {
      console.error('URLHandler: Error updating URL:', error);
    }
  }

  /**
   * Handle browser back/forward navigation
   */
  setupBrowserNavigation() {
    window.addEventListener('popstate', (event) => {
      console.log('URLHandler: Browser navigation detected, syncing filters');
      this.loadFromURL();
      
      // Trigger filter application after URL state change
      // This ensures the map updates when user uses browser back/forward
      this.triggerFilterApplication();
    });
  }

  /**
   * Set up listeners for filter changes to update URL
   */
  setupFilterChangeListeners() {
    // Subscribe to filter store changes
    useFilterStore.subscribe((state, prevState) => {
      // Only update URL if filter-related state changed (not viewport)
      const filterKeys = getInternalFilterParams();
      const hasFilterChanges = filterKeys.some(key => state[key] !== prevState[key]);
      
      if (hasFilterChanges) {
        // Debounce URL updates to avoid too many history entries
        clearTimeout(this.urlUpdateTimeout);
        this.urlUpdateTimeout = setTimeout(() => {
          this.updateURL();
        }, 500);
      }
    });
  }

  /**
   * Trigger filter application after URL changes
   * This integrates with the FilterManager to apply filters when URL changes
   */
  triggerFilterApplication() {
    // Import FilterManager dynamically to avoid circular dependencies
    import('../services/filterManager.js').then(({ filterManagerInstance }) => {
      filterManagerInstance.applyFilters(
        // Success callback
        (data) => {
          console.log('URLHandler: Applied filters after URL change');
        },
        // Error callback
        (error) => {
          console.error('URLHandler: Failed to apply filters after URL change:', error);
        },
        false // No debounce for URL-triggered changes
      );
    }).catch(error => {
      console.error('URLHandler: Error importing FilterManager:', error);
    });
  }

  /**
   * Generate shareable URL for current filter state
   * @returns {string} Complete URL with current filters
   */
  getShareableURL() {
    const { getURLParams } = useFilterStore.getState();
    const params = getURLParams();
    
    const baseURL = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
    return params.toString() ? `${baseURL}?${params.toString()}` : baseURL;
  }

  /**
   * Parse and validate URL parameters
   * @param {string} url - URL to parse (optional, defaults to current URL)
   * @returns {Object} Parsed and validated filter parameters
   */
  parseURL(url = window.location.href) {
    try {
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search);
      
      const parsed = {
        mineralType: params.get('mineralType') || 'all',
        commodities: params.get('commodities')?.split(',').filter(Boolean) || [],
        countries: params.get('countries')?.split(',').filter(Boolean) || [],
        mineTypes: params.get('mineTypes')?.split(',').filter(Boolean) || [],
        status: params.get('status')?.split(',').filter(Boolean) || ['active', 'development']
      };
      
      // Validate mineral type
      if (!['all', 'critical', 'non-critical'].includes(parsed.mineralType)) {
        parsed.mineralType = 'all';
      }
      
      return parsed;
    } catch (error) {
      console.error('URLHandler: Error parsing URL:', error);
      return {
        mineralType: 'all',
        commodities: [],
        countries: [],
        mineTypes: [],
        status: ['active', 'development']
      };
    }
  }

  /**
   * Cleanup method
   */
  destroy() {
    if (this.urlUpdateTimeout) {
      clearTimeout(this.urlUpdateTimeout);
    }
    
    window.removeEventListener('popstate', this.setupBrowserNavigation);
    this.isInitialized = false;
    console.log('URLHandler: Destroyed');
  }
}

// Export singleton instance
export const urlHandlerInstance = new URLHandler();